<template>
  <bizgain-component-layout
    :is-next-btn-enabled="true"
    :is-content-top="true"
    :col-width="{ 'mx-5 px-4': !$vuetify.breakpoint.xsOnly, 'mx-2': $vuetify.breakpoint.xsOnly }"
    view-port-height="170px"
  >
    <template v-slot:heading>
      {{ $t('message.bizgainOnboarding.businessDetailHeading') }}
    </template>
    <template v-slot:sub-heading>
      {{ $t('message.bizgainOnboarding.businessDetailSubHeading') }}
    </template>
    <template v-slot:content>
      <v-flex xs12 class="extract-business-details">
        <currency-preference-dialog :dialog.sync="isCurrencyPreferenceDialog" @regionSelected="handleRegionSelected" />
        <v-layout class="checkbox-block">
          <v-checkbox color="primary" v-model="isWebsiteSelected">
            <template v-slot:label>
              <v-img
                :src="websiteIcon"
                :lazy-src="websiteIcon"
                alt="Website Icon"
                width="24"
                height="24"
              />
              <div class="ml-1 label">
                {{ $t('message.bizgainOnboarding.yourBusinessWebsite') }}
              </div>
            </template>
          </v-checkbox>
        </v-layout>
        <v-layout v-if="isWebsiteSelected">
          <v-flex xs12 class="business-details-input ml-1">
            <v-text-field
              placeholder="Ex: https://businesswebsite.com"
              name="businessWebsiteUrl"
              class="pt-0 mt-0 website-url"
              v-model="businessWebsiteUrl"
              autocapitalize="off"
              v-validate="'url|max:100'"
              :data-vv-as="$tc('message.bizgainOnboarding.websiteUrl')"
              :error-messages="errors.first('businessWebsiteUrl')? $tc('message.bizgainOnboarding.websiteUrlValidationMessage'):null"
            />
          </v-flex>
        </v-layout>
        <v-layout class="checkbox-block">
          <v-checkbox color="primary" v-model="isGoogleMapsSelected">
            <template v-slot:label>
              <v-img
                :src="googleMapsIcon"
                :lazy-src="googleMapsIcon"
                alt="Google Maps Icon"
                width="24"
                height="24"
              />
              <div class="ml-1 label">
                {{ $tc('message.bizgainOnboarding.selectYourBusinessfromGoogleMap') }}
              </div>
              <div id="g-map-container" />
            </template>
          </v-checkbox>
        </v-layout>
        <v-layout v-if="isGoogleMapsSelected">
          <v-flex xs12 class="business-details-input ml-1">
            <v-layout>
              <v-flex xs12>
                <v-autocomplete
                  v-model="location"
                  :search-input.sync="search"
                  hide-no-data
                  :items="searchResults"
                  :no-filter="true"
                  clearable
                  placeholder="Search for your business in Google Maps"
                  @change="getPlaceDetails"
                  @click:clear="clearSearchResult"
                  :return-object="true"
                  class="pt-0 mt-0"
                  :menu-props="{ maxHeight: 170, offsetY: true, contentClass: 'google-menu-content' }"
                  append-icon="keyboard_arrow_down"
                  :hint="$t('message.bizgainOnboarding.multipleLocationsAlert')"
                  persistent-hint
                >
                  <v-layout
                    justify-center
                    align-center
                    slot="append-item"
                  >
                    <div>
                      <v-img
                        width="190"
                        height="30"
                        :src="poweredByGoogleIcon"
                      />
                    </div>
                  </v-layout>
                </v-autocomplete>
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-flex>
    </template>
    <template v-slot:btn-content>
      <v-flex xs12>
        <v-layout :class="{ 'column reverse': $vuetify.breakpoint.xsOnly }">
          <v-flex :class="{ 'xs2 nxt-btn-margin mt-0': !$vuetify.breakpoint.xsOnly, 'xs12': $vuetify.breakpoint.xsOnly, 'pl-2': $vuetify.breakpoint.smOnly }">
            <v-btn
              depressed
              class="mx-0 w-100 next-btn-color primary-btn-xs"
              :disabled="disableNextButton"
              @click="checkBusinessLocationRegion"
              :loading="isBusinessDetailsExtracted"
            >
              {{ $t('message.bizgainOnboarding.next') }}
            </v-btn>
          </v-flex>
        </v-layout>
      </v-flex>
    </template>
  </bizgain-component-layout>
</template>
<script>
import {
  VLayout,
  VFlex,
  VTextField,
  VAutocomplete,
  VCheckbox,
  VImg,
  VBtn
} from 'vuetify/lib';
import { GoogleMapHelper } from 'Features/bizgainOnboarding/helpers/googleMapHelper';
import { mapActions, mapGetters } from 'vuex';
import clearErrorByField from 'Mixins/clearErrorByField';
import { bizgainOnboardingImage, weekDays, bizgainOnboardingRoutes, bizgainConfig, signupFlowSteps, onboardingSections } from 'Features/bizgainOnboarding/constants';
import BizgainComponentLayout from 'Components/BizgainComponentLayout/BizgainComponentLayout.vue';
import CurrencyPreferenceDialog from '../../../components/CurrencyPreferenceDialog';
import { Chat } from 'Mixins/chat';
import onboardingRedirect  from 'Mixins/onboardingRedirect';

export default {
  mixins: [clearErrorByField, Chat, onboardingRedirect],
  components: {
    VLayout,
    VFlex,
    VTextField,
    VAutocomplete,
    VCheckbox,
    VBtn,
    VImg,
    BizgainComponentLayout,
    CurrencyPreferenceDialog
  },
  data() {
    return {
      businessWebsiteUrl: null,
      searchResults: [],
      currentLat: null,
      currentLng: null,
      search: null,
      location: null,
      googleMapObj: null,
      googleMapHelper: null,
      placeDetails: null,
      googleSessionToken: null,
      bizgainOnboardingImage,
      weekDays: weekDays,
      bizgainOnboardingRoutes,
      isBusinessDetailsExtracted: false,
      websiteIcon: bizgainConfig.websiteIcon,
      googleMapsIcon: bizgainConfig.googleMapsIcon,
      extractedBusinessDetailsOptions: null,
      poweredByGoogleIcon: bizgainConfig.poweredByGoogleIcon,
      isCurrencyPreferenceDialog: false
    }
  },
  created() {
    if(!this.$route.query.agentid && localStorage.getItem('isAgentFlow')){
      localStorage.removeItem('isAgentFlow');
    }
    this.$nextTick(async () => {
      await this.mapsInit();
    });
    if (this.websiteUrl) {
      this.businessWebsiteUrl = this.websiteUrl;
    }
    if (this.googleSelectedLocation) {
      this.searchResults.push(this.googleSelectedLocation);
      this.location = this.googleSelectedLocation;
    }
    this.extractedBusinessDetailsOptions = this.businessDetailsOptions;

    this.setSignUpSteps(signupFlowSteps);
    this.setCurrentRoute(bizgainOnboardingRoutes.ExtractBusinessDetails.name);
    this.setCurrentSection(onboardingSections.BUSINESSDETAILS);
    localStorage.removeItem('phoneNumbers');
  },
  computed: {
    ...mapGetters(['currentStep', 'totalStepCount', 'signUpSteps', 'websiteUrl', 'googleSelectedLocation', 'isError', 'businessDetailsOptions', 'isCustomUploadedBusinessLogo', 'portalDomain', 'region', 'isRegionChangePromptTriggered']),
    isBusinessDetailsProvided() {
      return (this.businessWebsiteUrl && !(this.errors && this.errors.first('businessWebsiteUrl'))) || this.placeDetails;
    },
    disableNoneOfTheAbove(){
      return this.isWebsiteSelected || this.isGoogleMapsSelected
    },
    disableNextButton(){
      return !((this.isWebsiteSelected || this.isGoogleMapsSelected) && ((this.businessWebsiteUrl && !(this.errors && this.errors.first('businessWebsiteUrl'))) || this.googleSelectedLocation));
    },
    isWebsiteSelected: {
      get(){
        return this.businessDetailsOptions.isWebsiteSelected;
      },
      set(value){
        if(!value){
          this.clearBusinessWebsite();
        }
        this.extractedBusinessDetailsOptions.isWebsiteSelected = value;
        if(value)
          this.extractedBusinessDetailsOptions.isNoneOfTheAboveSelected = false;
        this.setBusinessDetailsOptions(this.extractedBusinessDetailsOptions);
      }
    },
    isGoogleMapsSelected: {
      get(){
        return this.businessDetailsOptions.isGoogleMapsSelected;
      },
      set(value){
        if(!value){
          this.clearSearchResult();
        }
        this.extractedBusinessDetailsOptions.isGoogleMapsSelected = value;
        if(value)
          this.extractedBusinessDetailsOptions.isNoneOfTheAboveSelected = false;
        this.setBusinessDetailsOptions(this.extractedBusinessDetailsOptions);
      }
    },
    isNoneOfTheAboveSelected: {
      get(){
        return this.businessDetailsOptions.isNoneOfTheAboveSelected;
      },
      set(value){
        this.extractedBusinessDetailsOptions.isNoneOfTheAboveSelected = value;
        this.setBusinessDetailsOptions(this.extractedBusinessDetailsOptions);
      }
    }
  },
  methods: {
    ...mapActions(['setSignUpSteps', 'setCurrentSection', 'setCurrentRoute', 'setBusinessDetails', 'setGoogleSelectedLocation', 'setWebsiteUrl', 'setBusinessDetailsOptions', 'setBusinessLogo', 'setGooglePlaceDetails', 'setRegionChangePromptTriggered']),
    async mapsInit() {
      this.googleMapHelper = new GoogleMapHelper();
      let googleMapObj = await this.googleMapHelper.mapInitalize(document.getElementById('g-map-container'));
      if (googleMapObj) {
        this.googleMapObj = googleMapObj;
        this.googleSessionToken = this.googleMapObj.sessionToken;
      }
      if (this.googleSelectedLocation && this.location) {
        await this.getPlaceDetails();
      }
      let currentLocationDetails = await this.googleMapHelper.getGoogleCurrentLocationDetails();
      this.currentLat = currentLocationDetails.latitude;
      this.currentLng = currentLocationDetails.longitude;
    },
    displaySuggestions (predictions, status) {
      this.searchResults = this.googleMapHelper.getGoogleMapSuggesstion(predictions, status);
    },
    async getPlaceDetails() {
      if (!this.location || !this.location.value) {
        return;
      }
      this.setGoogleSelectedLocation(this.location);
      let placeDetails = await this.googleMapHelper.getGoogleMapPlaceDetails(this.location.value, this.googleSessionToken, this.googleMapObj.map);
      if (placeDetails) {
        this.placeDetails = placeDetails.googlePlaceDetails;
        this.googleSessionToken = placeDetails.sessionToken;
        if(this.placeDetails && this.placeDetails.photos){
          var imageUrls = [];
          for (var i = 0 ; i < this.placeDetails.photos.length ; i++)
          {
            var imageUrl = this.placeDetails.photos[i].getUrl({ maxWidth: 1080, maxHeight: 1080 });
            imageUrls.push(imageUrl);
          }
          this.placeDetails.imageUrls = imageUrls;
        }
        this.setGooglePlaceDetails(this.placeDetails);
      }
    },
    async checkBusinessLocationRegion(){
      if (this.placeDetails && this.placeDetails.addressComponents)
      {
        let country = this.placeDetails.addressComponents.find(x => x.types.includes('country'));
        await this.updateUserProperties({ countryName: country.longName });
        if (country && country.shortName && country.shortName !== this.region) {
          this.isCurrencyPreferenceDialog = true;
          this.setRegionChangePromptTriggered(true);
        } else {
          await this.fetchBusinessDetails();
        }
      } else {
        await this.fetchBusinessDetails();
      }
    },
    async handleRegionSelected(){
      await this.fetchBusinessDetails();
    },
    async fetchBusinessDetails() {
      let isValid = await this.$validator.validate();
      if(!this.isCustomUploadedBusinessLogo){
        this.setBusinessLogo(null);
      }
      if (isValid) {
        try {
          this.isBusinessDetailsExtracted = true;
          if(this.isGoogleMapsSelected && this.placeDetails){
            this.setGoogleSelectedLocation(this.location);
            if (this.placeDetails.hours && this.placeDetails.hours.periods) {
              let openingHours = null;
              if (Array.isArray(this.placeDetails.hours.periods) &&
              this.placeDetails.hours.periods.length === 1 &&
              !this.placeDetails.hours.periods[0].close
              ) {
                openingHours = this.googleMapHelper.generate24hrsOpeningHours();
              } else {
                openingHours = this.googleMapHelper.convertOpeningHoursToDXFormate(this.placeDetails.hours.periods);
              }
              if (openingHours && Array.isArray(openingHours) && openingHours.length > 0) {
                this.placeDetails.openingHours = openingHours;
              }
            }
            await this.updateUserProperties({ gmb: this.placeDetails.mapUrl, businessPhoneNumber: this.placeDetails.internationalPhoneNumber, website: this.placeDetails.website });
          }
          else{
            this.setGoogleSelectedLocation(null);
            this.setGooglePlaceDetails(null);
            this.placeDetails = null;
          }

          if(this.isWebsiteSelected && this.businessWebsiteUrl){
            if(this.placeDetails){
              await this.updateUserProperties({ website: this.placeDetails.website });
            }
            this.setWebsiteUrl(this.businessWebsiteUrl);
          }
          else{
            this.setWebsiteUrl(null);
            this.businessWebsiteUrl = null;
          }

          let payload = {
            websiteUrl: this.businessWebsiteUrl,
            googlePlaceDetails: this.placeDetails,
            region: this.region,
            queryParameters: { ...this.$route.query }
          };
          if((this.businessWebsiteUrl && !(this.errors && this.errors.first('businessWebsiteUrl'))) || this.placeDetails || !this.isEmpty(this.$route.query)){
            await this.setBusinessDetails(payload);
          }
          if (!this.isError) {
            this.redirect({ isAppFlow: true }, {}, false, null, null);
          }
          else{
            this.isBusinessDetailsExtracted = false;
          }
        } catch {
          this.isBusinessDetailsExtracted = false;
        }
      }
    },
    clearBusinessWebsite(){
      this.setWebsiteUrl(null);
      this.businessWebsiteUrl = null;
    },
    clearSearchResult() {
      this.location = null;
      this.search = null;
      this.placeDetails = null;
      this.setGooglePlaceDetails(null);
      this.setGoogleSelectedLocation(null);
    },
    isEmpty(obj) {
      for (var item in obj) { return false; }
      return true;
    },
    discardChanges(){
      this.isWebsiteSelected = false;
      this.businessWebsiteUrl = null;
      this.isGoogleMapsSelected = false;
      this.clearSearchResult();
    }
  },
  watch: {
    // Check Google Place API JS doc here: https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#QueryAutocompletionRequest
    search(newValue, oldValue) {
      if (newValue && this.googleMapObj && this.googleMapObj.service && newValue != oldValue) {
        var request = {
          input: this.search,
          sessionToken: this.googleSessionToken,
          location: new google.maps.LatLng(this.currentLat, this.currentLng),
          radius: 5000
        };
        this.googleMapObj.service.getPlacePredictions(request, this.displaySuggestions)
      }
    }
  }
}
</script>
<style lang="scss" scoped>
::v-deep.extract-business-details {
    .facbook-login-btn {
      cursor: pointer;
    }
    .card-height {
      height: 80px;
    }
  .website-url{
     .v-input__slot{
        margin: 0 !important;
        padding: 0 !important;
     }
  }
  .checkbox-block{
    .v-input--selection-controls {
      margin: 0 !important;
      padding: 0 !important;
      height: 50px;
      .v-input__slot{
        margin: 0 !important;
        padding: 0 !important;
     }
    }
  }
}

::v-deep .v-input__control {
  margin-top: 8px !important;
}

.business-details-input {
  padding-bottom: 12px !important;
  margin-top: -16px !important;
}
</style>